import { useState } from "react";
import { Legend } from "./Legend";
import { BOSH_USLUB, yumaloq } from "./scale";

export interface LineSeries {
	key: string;
	label: string;
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	/** `values[i]` ↔ `xLabels[i]`. ⚠️ `null` — MA'LUMOTSIZ nuqta, `0` EMAS. */
	values: (number | null)[];
	/** Uzuq chiziq (masalan oldingi davr). */
	dashed?: boolean;
	/** Chiziq ostini och rang bilan to'ldirish. */
	area?: boolean;
}

export interface LineChartProps {
	xLabels: string[];
	series: LineSeries[];
	/** ⚠️ Shkala ANIQ beriladi — avtomatik shkala 92↔93 ni «qulash» qilib ko'rsatadi. */
	yMin: number;
	yMax: number;
	yTicks: number[];
	formatY: (v: number) => string;
	target?: { value: number; label: string };
	/** Chizma maydoni balandligi, px. */
	height?: number;
	/** Standart — seriya nomlari (reja imzosidan kengaytirilgan). */
	ariaLabel?: string;
	/** Hech bir nuqta bo'lmasa chiqadigan matn (reja imzosidan kengaytirilgan). */
	empty?: string;
}

interface Nuqta {
	x: number;
	y: number;
}

const Y_UQI_KENGLIGI = 40;

function xHisobi(indeks: number, n: number): number {
	return n <= 1 ? 50 : yumaloq((indeks / (n - 1)) * 100);
}

/** Uzluksiz bo'laklar — `null` da uziladi. */
function bolaklar(values: (number | null)[], n: number, yHisobi: (v: number) => number): Nuqta[][] {
	const natija: Nuqta[][] = [];
	let joriy: Nuqta[] = [];

	for (let i = 0; i < n; i++) {
		const v = values[i];

		if (v === null || v === undefined || !Number.isFinite(v)) {
			if (joriy.length > 0) {
				natija.push(joriy);
				joriy = [];
			}

			continue;
		}

		joriy.push({ x: xHisobi(i, n), y: yHisobi(v) });
	}

	if (joriy.length > 0) {
		natija.push(joriy);
	}

	return natija;
}

function chiziqYoli(qismlar: Nuqta[][]): string {
	return qismlar
		.map((qism) => {
			// ⚠️ Yakka nuqta — nol uzunlikli chiziq: `round` uchi uni nuqta qilib chizadi.
			const royxat = qism.length === 1 ? [qism[0], qism[0]] : qism;
			return royxat.map((p, i) => `${i === 0 ? "M" : "L"}${p?.x},${p?.y}`).join(" ");
		})
		.join(" ");
}

function maydonYoli(qism: Nuqta[]): string {
	const boshi = qism[0];
	const oxiri = qism.at(-1);

	if (!(boshi && oxiri) || qism.length < 2) {
		return "";
	}

	return `M${boshi.x},100 ${qism.map((p) => `L${p.x},${p.y}`).join(" ")} L${oxiri.x},100 Z`;
}

/**
 * Ko'p seriyali chiziqli diagramma (inline SVG).
 *
 * ⚠️ **SVG `viewBox="0 0 100 100"` + `preserveAspectRatio="none"`** —
 * kenglikka cho'ziladi. Shu sababli SVG ICHIDA faqat yo'llar va
 * chiziqlar (`vector-effect="non-scaling-stroke"`); matnlar (o'q
 * belgilari, tooltip) HTML — aks holda cho'zilib ketardi.
 *
 * ⚠️ **`null` chiziqni uzadi** (yangi `M`), ma'lumotsiz nuqta ustidan
 * yolg'on interpolyatsiya yo'q. Shkaladan tashqari qiymat chetga
 * qisiladi.
 *
 * ⚠️ Hover — har X uchun ko'rinmas `rect` zonasi; crosshair va
 * tooltip faqat mavjud qiymatlarni ko'rsatadi.
 *
 * ⚠️ `data-line-*` — TEST KONTRAKTI.
 */
export function LineChart({
	xLabels,
	series,
	yMin,
	yMax,
	yTicks,
	formatY,
	target,
	height = 180,
	ariaLabel,
	empty,
}: LineChartProps) {
	const [faol, setFaol] = useState<number | null>(null);
	const n = xLabels.length;
	const maLumotBor = series.some((s) => s.values.slice(0, n).some((v) => v !== null));

	if (n === 0 || !maLumotBor) {
		return (
			<div className="py-4 text-center text-xs" style={BOSH_USLUB}>
				{empty}
			</div>
		);
	}

	const oraliq = yMax - yMin;
	const yHisobi = (v: number): number => {
		// ⚠️ Teng chegaralar — chiziq o'rtada, NaN EMAS.
		if (!(oraliq > 0)) {
			return 50;
		}

		const cheklangan = Math.min(yMax, Math.max(yMin, v));
		return yumaloq(100 - ((cheklangan - yMin) / oraliq) * 100);
	};
	const qadam = n <= 1 ? 100 : 100 / (n - 1);
	const yorliqQadami = Math.max(1, Math.ceil(n / 12));
	const faolX = faol === null ? null : xHisobi(faol, n);

	return (
		<div>
			<div className="relative flex" style={{ height: `${height}px` }}>
				<div className="relative shrink-0" style={{ width: Y_UQI_KENGLIGI }}>
					{yTicks.map((tick) => (
						<span
							key={tick}
							data-line-tick
							className="absolute right-1.5 -translate-y-1/2 text-[10px] tabular-nums"
							style={{ top: `${yHisobi(tick)}%`, color: "var(--chart-axis)" }}
						>
							{formatY(tick)}
						</span>
					))}
				</div>

				<div className="relative min-w-0 flex-1">
					<svg
						role="img"
						aria-label={ariaLabel ?? series.map((s) => s.label).join(", ")}
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						className="absolute inset-0 h-full w-full"
						style={{ overflow: "visible" }}
						onMouseLeave={() => setFaol(null)}
					>
						{yTicks.map((tick) => (
							<line
								key={tick}
								data-line-grid
								x1={0}
								x2={100}
								y1={yHisobi(tick)}
								y2={yHisobi(tick)}
								stroke="var(--chart-grid)"
								strokeWidth={1}
								vectorEffect="non-scaling-stroke"
							/>
						))}

						{series.map((s) =>
							s.area
								? bolaklar(s.values, n, yHisobi).map((qism) =>
										qism.length >= 2 ? (
											<path
												key={`${s.key}-fon-${qism[0]?.x}`}
												data-line-area
												d={maydonYoli(qism)}
												fill={s.color}
												fillOpacity={0.1}
												stroke="none"
											/>
										) : null
									)
								: null
						)}

						{target ? (
							<line
								data-line-target
								x1={0}
								x2={100}
								y1={yHisobi(target.value)}
								y2={yHisobi(target.value)}
								stroke="var(--chart-axis)"
								strokeWidth={1}
								strokeDasharray="4 3"
								vectorEffect="non-scaling-stroke"
							/>
						) : null}

						{series.map((s) => (
							<path
								key={s.key}
								data-line-series={s.key}
								d={chiziqYoli(bolaklar(s.values, n, yHisobi))}
								fill="none"
								stroke={s.color}
								strokeWidth={2}
								strokeLinejoin="round"
								strokeLinecap="round"
								strokeDasharray={s.dashed ? "5 4" : undefined}
								vectorEffect="non-scaling-stroke"
							/>
						))}

						{faolX === null ? null : (
							<line
								data-line-crosshair
								x1={faolX}
								x2={faolX}
								y1={0}
								y2={100}
								stroke="var(--chart-axis)"
								strokeWidth={1}
								vectorEffect="non-scaling-stroke"
							/>
						)}

						{xLabels.map((yorliq, i) => {
							const markaz = xHisobi(i, n);
							const chap = Math.max(0, markaz - qadam / 2);
							const ong = Math.min(100, markaz + qadam / 2);

							return (
								// biome-ignore lint/a11y/noStaticElementInteractions: faqat sichqoncha hover'i — qiymatlar ekran o'quvchisiga `aria-label` va legend orqali yetadi
								<rect
									key={`${yorliq}-${markaz}`}
									data-line-hit
									x={yumaloq(chap)}
									y={0}
									width={yumaloq(ong - chap)}
									height={100}
									fill="transparent"
									onMouseEnter={() => setFaol(i)}
								/>
							);
						})}
					</svg>

					{target ? (
						<span
							className="pointer-events-none absolute right-0 -translate-y-full text-[10px]"
							style={{ top: `${yHisobi(target.value)}%`, color: "var(--chart-axis)" }}
						>
							{target.label}
						</span>
					) : null}

					{/*
					 * ⚠️ Yakka nuqta markeri HTML'da: Chromium `non-scaling-stroke`
					 * bilan nol uzunlikli yo'lni CHIZMAYDI, SVG `circle` esa
					 * `preserveAspectRatio="none"` da ellipsga cho'ziladi.
					 */}
					{series.flatMap((s) =>
						bolaklar(s.values, n, yHisobi)
							.filter((qism) => qism.length === 1)
							.map((qism) => (
								<span
									key={`${s.key}-nuqta-${qism[0]?.x}`}
									data-line-dot={s.key}
									aria-hidden="true"
									className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
									style={{
										left: `${qism[0]?.x}%`,
										top: `${qism[0]?.y}%`,
										width: 8,
										height: 8,
										backgroundColor: s.color,
										boxShadow: "0 0 0 2px var(--chart-surface)",
									}}
								/>
							))
					)}

					{faol === null || faolX === null ? null : (
						<Tooltip
							x={faolX}
							sarlavha={xLabels[faol] ?? ""}
							qatorlar={series.flatMap((s) => {
								const v = s.values[faol];
								return v === null || v === undefined
									? []
									: [{ key: s.key, label: s.label, color: s.color, matn: formatY(v) }];
							})}
						/>
					)}
				</div>
			</div>

			<div className="relative mt-1 h-3.5" style={{ marginLeft: Y_UQI_KENGLIGI }}>
				{xLabels.map((yorliq, i) =>
					i % yorliqQadami === 0 || i === n - 1 ? (
						<span
							key={`${yorliq}-${xHisobi(i, n)}`}
							data-line-xlabel
							className="absolute -translate-x-1/2 whitespace-nowrap text-[10px]"
							style={{ left: `${xHisobi(i, n)}%`, color: "var(--chart-axis)" }}
						>
							{yorliq}
						</span>
					) : null
				)}
			</div>

			<div className="mt-2">
				<Legend
					items={series.map((s) => ({ label: s.label, color: s.color, shape: "line" as const }))}
				/>
			</div>
		</div>
	);
}

function Tooltip({
	x,
	sarlavha,
	qatorlar,
}: {
	x: number;
	sarlavha: string;
	qatorlar: { key: string; label: string; color: string; matn: string }[];
}) {
	return (
		<div
			data-line-tooltip
			className="pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-md px-2 py-1 text-[11px] shadow"
			style={{
				left: `${x}%`,
				// ⚠️ O'ng yarmida tooltip chap tomonga ochiladi — konteynerdan chiqmasin.
				transform: x > 50 ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
				backgroundColor: "var(--chart-surface)",
				color: "var(--chart-ink)",
				border: "1px solid var(--chart-grid)",
			}}
		>
			<div className="font-medium">{sarlavha}</div>
			{qatorlar.map((q) => (
				<div key={q.key} className="flex items-center gap-1.5">
					<span
						aria-hidden="true"
						className="inline-block"
						style={{ width: 10, height: 2, backgroundColor: q.color }}
					/>
					<span>{q.label}</span>
					<strong className="ml-auto pl-2 tabular-nums">{q.matn}</strong>
				</div>
			))}
		</div>
	);
}
